/**
 * Leaderboard Page (/leaderboard)
 * Curated, privacy-respecting view:
 * - Top 5 Champions (real first name + initial — earned visibility)
 * - Your Position card with Tier progress
 * - Anonymous Rivals (1 above + 1 below)
 * - Achievements / Badges grid
 *
 * No total student count is shown (preserves "infinite scale" brand perception).
 * Full leaderboard list is intentionally hidden — only Top 5 + your context.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Trophy, Crown, Flame, Award, ChevronLeft,
  Sparkles, BookOpen, Zap, Lock, CheckCircle2, ChevronUp, ChevronDown
} from 'lucide-react';
import SEO from '../components/SEO';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SOURCE_TABS = [
  { value: 'combined', label: 'Mastery', color: 'from-violet-500 to-fuchsia-500' },
  { value: 'labs', label: 'Labs', color: 'from-emerald-500 to-teal-500' },
  { value: 'mocks', label: 'Mock Tests', color: 'from-blue-500 to-indigo-500' }
];

const PERIOD_TABS = [
  { value: 'all', label: 'All Time' },
  { value: '30', label: '30 days' },
  { value: '7', label: 'This Week' }
];

const TIER_STYLES = {
  bronze: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', icon: 'text-orange-500' },
  silver: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300', icon: 'text-slate-500' },
  gold: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300', icon: 'text-amber-600' },
  diamond: { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-300', icon: 'text-cyan-600' }
};

// Tier color → tailwind classes for "Your Tier" card
const TIER_COLOR_MAP = {
  amber:    { from: 'from-amber-500',   to: 'to-orange-500',   text: 'text-amber-700',  ring: 'ring-amber-200' },
  cyan:     { from: 'from-cyan-500',    to: 'to-blue-500',     text: 'text-cyan-700',   ring: 'ring-cyan-200' },
  yellow:   { from: 'from-yellow-400',  to: 'to-amber-500',    text: 'text-yellow-700', ring: 'ring-yellow-200' },
  slate:    { from: 'from-slate-400',   to: 'to-slate-600',    text: 'text-slate-700',  ring: 'ring-slate-200' },
  orange:   { from: 'from-orange-400',  to: 'to-red-500',      text: 'text-orange-700', ring: 'ring-orange-200' },
  emerald:  { from: 'from-emerald-400', to: 'to-teal-500',     text: 'text-emerald-700', ring: 'ring-emerald-200' }
};

const ChampionRank = ({ rank, isYou }) => {
  if (rank === 1) return <span className="text-3xl drop-shadow" title="Gold">🥇</span>;
  if (rank === 2) return <span className="text-3xl drop-shadow" title="Silver">🥈</span>;
  if (rank === 3) return <span className="text-3xl drop-shadow" title="Bronze">🥉</span>;
  return (
    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm ${
      isYou ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-700'
    }`}>
      #{rank}
    </div>
  );
};

const LeaderboardPage = () => {
  const [source, setSource] = useState('combined');
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchChampions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const res = await fetch(
        `${API_BASE}/api/leaderboard/champions?source=${source}&period=${period}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('Champions fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [source, period]);

  const fetchBadges = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const res = await fetch(`${API_BASE}/api/leaderboard/badges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setBadges(await res.json());
    } catch (e) {
      console.error('Badges fetch failed', e);
    }
  }, []);

  useEffect(() => { fetchChampions(); }, [fetchChampions]);
  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  const champions = data?.champions || [];
  const you = data?.you;
  const tier = data?.tier;
  const rivalAbove = data?.rival_above;
  const rivalBelow = data?.rival_below;

  const tierColors = tier?.current?.color
    ? TIER_COLOR_MAP[tier.current.color] || TIER_COLOR_MAP.emerald
    : TIER_COLOR_MAP.emerald;

  // Tier progress bar — calculate fill % based on rank progression to next tier
  const tierProgress = (() => {
    if (!tier?.current?.max_rank || !you?.rank) return 0;
    const currentMax = tier.current.max_rank;
    const nextMax = tier.next?.max_rank || 0;
    if (!nextMax) return 100;
    // rank close to nextMax means high progress (e.g., rank 12 in Gold(25), need to reach Diamond(10))
    // Progress = (currentMax - rank) / (currentMax - nextMax)
    const span = Math.max(1, currentMax - nextMax);
    const filled = Math.max(0, currentMax - you.rank);
    return Math.min(100, Math.round((filled / span) * 100));
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <SEO
        title="Leaderboard — Top Champions | Grras Lab Platform"
        description="See the top 5 champions and track your rank against fellow students based on verified lab completions and mock test scores."
      />

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard"
            className="w-10 h-10 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center transition"
            data-testid="leaderboard-back-btn"
          >
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <Trophy className="w-8 h-8 text-amber-500" />
              Leaderboard
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Where champions earn their spot. Verified data only — no shortcuts.
            </p>
          </div>
        </div>

        {/* Source Tabs */}
        <div className="flex flex-wrap gap-2">
          {SOURCE_TABS.map(tab => {
            const isActive = source === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => setSource(tab.value)}
                className={`px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
                  isActive
                    ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                    : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`source-tab-${tab.value}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Period Tabs */}
        <div className="flex gap-2">
          {PERIOD_TABS.map(p => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                period === p.value
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`period-tab-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Top 5 Champions */}
        <Card className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border-amber-200 shadow-md overflow-hidden">
          <CardHeader className="border-b border-amber-200/60">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                <Crown className="w-5 h-5 text-amber-500" />
                Top 5 Champions
              </CardTitle>
              <Badge className="bg-amber-500 text-white border-amber-600 hover:bg-amber-600">
                ⭐ Verified
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-16 text-slate-500">Loading champions...</div>
            ) : champions.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-slate-500 mb-2">No champions yet for this filter.</p>
                <p className="text-xs text-slate-400">Be the first — complete a lab to claim the throne.</p>
              </div>
            ) : (
              <div className="divide-y divide-amber-100">
                {champions.map((entry) => (
                  <div
                    key={entry.rank}
                    className={`flex items-center justify-between px-6 py-4 transition ${
                      entry.is_you
                        ? 'bg-gradient-to-r from-amber-100 to-orange-100 border-l-4 border-amber-500'
                        : 'hover:bg-amber-50/50'
                    }`}
                    data-testid={`champion-row-${entry.rank}`}
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="w-10 flex justify-center shrink-0">
                        <ChampionRank rank={entry.rank} isYou={entry.is_you} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`font-bold truncate text-base ${entry.is_you ? 'text-amber-900' : 'text-slate-900'}`}>
                          {entry.display_name}
                          {entry.is_you && <span className="ml-2 text-xs text-amber-600 font-bold uppercase tracking-wider">(You)</span>}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-600 mt-0.5">
                          <span>{entry.labs_completed} labs</span>
                          {entry.mocks_passed > 0 && <span>· {entry.mocks_passed} mocks</span>}
                          {entry.streak_days > 0 && (
                            <span className="flex items-center gap-0.5 text-orange-600 font-semibold">
                              · <Flame className="w-3 h-3" />{entry.streak_days}d
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-2xl font-bold text-slate-900">
                        {entry.mastery_score?.toLocaleString() || 0}
                      </p>
                      <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">Score</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Your Position + Tier */}
        {you && tier && (
          <Card className={`bg-white border-2 ${tierColors.ring} shadow-md`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tierColors.from} ${tierColors.to} flex flex-col items-center justify-center shadow-md shrink-0`}>
                    <span className="text-white/80 text-[10px] font-semibold tracking-wider uppercase leading-none">Rank</span>
                    <span className="text-white text-2xl font-bold leading-none mt-0.5">#{you.rank}</span>
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs uppercase tracking-wider font-semibold ${tierColors.text}`}>Your Position</p>
                    <p className="text-xl font-bold text-slate-900 truncate">{you.display_name}</p>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-600 flex-wrap">
                      <span className="font-semibold text-emerald-700">{you.labs_completed} labs</span>
                      <span className="text-slate-300">·</span>
                      <span className="font-semibold text-blue-700">{you.mocks_passed}/{you.mocks_attempted} mocks</span>
                      {you.streak_days > 0 && (
                        <>
                          <span className="text-slate-300">·</span>
                          <span className="flex items-center gap-1 text-orange-600 font-semibold">
                            <Flame className="w-4 h-4" />{you.streak_days}d
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-xs uppercase tracking-wider font-semibold ${tierColors.text}`}>Mastery Score</p>
                  <p className="text-3xl font-bold text-slate-900">{you.mastery_score?.toLocaleString() || 0}</p>
                </div>
              </div>

              {/* Tier Progress */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{tier.current?.icon}</span>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Current Tier</p>
                      <p className={`text-lg font-bold ${tierColors.text}`}>{tier.current?.name}</p>
                    </div>
                  </div>
                  {tier.next && tier.ranks_to_next > 0 ? (
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Next: <span className="font-semibold text-slate-700">{tier.next.icon} {tier.next.name}</span></p>
                      <p className={`text-sm font-bold ${tierColors.text}`}>
                        {tier.ranks_to_next} {tier.ranks_to_next === 1 ? 'rank' : 'ranks'} to climb
                      </p>
                    </div>
                  ) : (
                    <Badge className="bg-amber-500 text-white">👑 Top tier reached</Badge>
                  )}
                </div>
                <div className="w-full h-2.5 bg-white rounded-full overflow-hidden border border-slate-200">
                  <div
                    className={`h-full bg-gradient-to-r ${tierColors.from} ${tierColors.to} rounded-full transition-all duration-700`}
                    style={{ width: `${tierProgress}%` }}
                    data-testid="tier-progress-bar"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rivals - Anonymous handles for privacy */}
        {(rivalAbove || rivalBelow) && (
          <Card className="bg-white border-slate-200 shadow-md">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                <Zap className="w-5 h-5 text-violet-500" />
                Your Rivals
                <span className="text-xs text-slate-400 font-normal ml-2">(identities hidden for privacy)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 divide-y divide-slate-100">
              {rivalAbove && (
                <div className="flex items-center justify-between px-6 py-4 hover:bg-emerald-50/40 transition" data-testid="rival-above">
                  <div className="flex items-center gap-3">
                    <ChevronUp className="w-5 h-5 text-emerald-500" />
                    <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm">
                      #{rivalAbove.rank}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{rivalAbove.handle}</p>
                      <p className="text-xs text-slate-500">{rivalAbove.labs_completed} labs · {rivalAbove.mastery_score.toLocaleString()} pts</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wider">Catch up</p>
                    <p className="text-sm font-bold text-slate-900">
                      {rivalAbove.labs_ahead > 0
                        ? `${rivalAbove.labs_ahead} ${rivalAbove.labs_ahead === 1 ? 'lab' : 'labs'} ahead`
                        : `${rivalAbove.score_gap} pts ahead`}
                    </p>
                  </div>
                </div>
              )}
              {you && (
                <div className="flex items-center justify-between px-6 py-3 bg-amber-50 border-l-4 border-amber-400" data-testid="rival-you">
                  <div className="flex items-center gap-3">
                    <span className="w-5"></span>
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center font-bold text-sm">
                      #{you.rank}
                    </div>
                    <div>
                      <p className="font-bold text-amber-900">{you.display_name} <span className="text-xs uppercase tracking-wider">(You)</span></p>
                      <p className="text-xs text-amber-700">{you.labs_completed} labs · {you.mastery_score.toLocaleString()} pts</p>
                    </div>
                  </div>
                </div>
              )}
              {rivalBelow && (
                <div className="flex items-center justify-between px-6 py-4 hover:bg-rose-50/40 transition" data-testid="rival-below">
                  <div className="flex items-center gap-3">
                    <ChevronDown className="w-5 h-5 text-rose-500" />
                    <div className="w-9 h-9 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-sm">
                      #{rivalBelow.rank}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{rivalBelow.handle}</p>
                      <p className="text-xs text-slate-500">{rivalBelow.labs_completed} labs · {rivalBelow.mastery_score.toLocaleString()} pts</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-rose-700 font-semibold uppercase tracking-wider">Stay ahead</p>
                    <p className="text-sm font-bold text-slate-900">
                      {rivalBelow.score_gap} pts behind
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Badges Section */}
        {badges && (
          <Card className="bg-white border-slate-200 shadow-md">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                  <Award className="w-5 h-5 text-violet-500" />
                  Achievements
                </CardTitle>
                <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                  {badges.earned_count} / {badges.total_count} unlocked
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              {badges.next_badge && (
                <div className="mb-6 bg-gradient-to-r from-violet-50 to-fuchsia-50 border border-violet-200 rounded-xl p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <div className="text-3xl">{badges.next_badge.icon}</div>
                      <div>
                        <p className="font-bold text-slate-900">Next: {badges.next_badge.name}</p>
                        <p className="text-xs text-slate-600">{badges.next_badge.description}</p>
                      </div>
                    </div>
                    <div className="flex-1 max-w-xs">
                      <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                        <span>{badges.next_badge.current} / {badges.next_badge.threshold}</span>
                        <span className="font-semibold text-violet-700">{badges.next_badge.progress_pct}%</span>
                      </div>
                      <div className="w-full h-2 bg-white rounded-full overflow-hidden border border-violet-100">
                        <div
                          className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full transition-all duration-500"
                          style={{ width: `${badges.next_badge.progress_pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {badges.all_badges?.map(b => {
                  const tierStyle = TIER_STYLES[b.tier] || TIER_STYLES.bronze;
                  return (
                    <div
                      key={b.id}
                      className={`relative rounded-xl p-3 border-2 transition ${
                        b.earned
                          ? `${tierStyle.bg} ${tierStyle.border} hover:scale-105`
                          : 'bg-slate-50 border-slate-200 grayscale opacity-60'
                      }`}
                      data-testid={`badge-${b.id}`}
                    >
                      <div className="text-3xl text-center mb-1">{b.icon}</div>
                      <p className={`text-center font-bold text-xs ${b.earned ? tierStyle.text : 'text-slate-500'}`}>
                        {b.name}
                      </p>
                      <p className="text-center text-[10px] text-slate-500 mt-0.5 leading-tight">
                        {b.description}
                      </p>
                      {b.earned ? (
                        <CheckCircle2 className={`absolute top-1.5 right-1.5 w-4 h-4 ${tierStyle.icon}`} />
                      ) : (
                        <>
                          <Lock className="absolute top-1.5 right-1.5 w-3 h-3 text-slate-400" />
                          <div className="mt-2 h-1 bg-white rounded-full overflow-hidden">
                            <div
                              className="h-full bg-slate-400 rounded-full"
                              style={{ width: `${b.progress_pct}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-center text-slate-500 mt-1">
                            {b.current}/{b.threshold}
                          </p>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CTA */}
        <div className="text-center pt-4 pb-8">
          <Link to="/courses">
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg" data-testid="leaderboard-cta-courses">
              <Sparkles className="w-4 h-4 mr-2" />
              Continue Learning to Climb the Ranks
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default LeaderboardPage;
